import React, { useState, useEffect, useContext } from 'react'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { Download, Eye, Calendar, Package, Trash2, CheckCircle } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import AdvancedImageViewer from '../components/AdvancedImageViewer'
import { format } from 'date-fns'

const PushedMedicineSets = () => {
  const { API } = useContext(AuthContext)
  const [activeTab, setActiveTab] = useState('sets') // 'sets' or 'downloads'
  const [medicineSets, setMedicineSets] = useState([])
  const [excelDownloads, setExcelDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedSet, setSelectedSet] = useState(null)
  const [showImageDialog, setShowImageDialog] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [selectedSets, setSelectedSets] = useState([])

  useEffect(() => {
    fetchPushedSets()
    fetchExcelDownloads()
  }, [])

  const fetchPushedSets = async () => {
    try {
      const response = await axios.get(`${API}/medicine-sets?pushed=true`)
      setMedicineSets(response.data)
    } catch (error) {
      console.error('Failed to fetch pushed medicine sets:', error)
      toast.error('Failed to load pushed medicine sets')
    } finally {
      setLoading(false)
    }
  }

  const fetchExcelDownloads = async () => {
    try {
      const response = await axios.get(`${API}/excel-downloads`)
      setExcelDownloads(response.data)
    } catch (error) {
      console.error('Failed to fetch excel downloads:', error)
      toast.error('Failed to load excel downloads')
    }
  }

  const handleViewImage = (set) => {
    setSelectedSet(set)
    setShowImageDialog(true)
  }

  const handleExport = async (setId, setName) => {
    try {
      toast.info('Generating Excel file...')
      const response = await axios.get(`${API}/medicine-sets/${setId}/export`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${setName}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Excel file downloaded successfully!')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export medicine set')
    }
  }

  const handleDelete = async (setId, setName) => {
    if (!window.confirm(`Are you sure you want to delete "${setName}"?`)) {
      return
    }

    setDeletingId(setId)
    try {
      await axios.delete(`${API}/medicine-sets/${setId}`)
      toast.success('Medicine set deleted successfully!')
      setMedicineSets(medicineSets.filter(s => s.id !== setId))
      setSelectedSets(selectedSets.filter(id => id !== setId))
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete medicine set')
    } finally {
      setDeletingId(null)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedSets.length === 0) {
      toast.error('Please select at least one medicine set')
      return
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedSets.length} medicine set(s)?`)) {
      return
    }

    try {
      await Promise.all(selectedSets.map(id => axios.delete(`${API}/medicine-sets/${id}`)))
      toast.success('Selected medicine sets deleted successfully!')
      setMedicineSets(medicineSets.filter(s => !selectedSets.includes(s.id)))
      setSelectedSets([])
    } catch (error) {
      console.error('Bulk delete error:', error)
      toast.error('Failed to delete selected medicine sets')
    }
  }

  const handleSelectSet = (setId, checked) => {
    if (checked) {
      setSelectedSets([...selectedSets, setId])
    } else {
      setSelectedSets(selectedSets.filter(id => id !== setId))
    }
  }

  if (loading) {
    return (
      <Layout title="Pushed Medicine Excel">
        <div className="card text-center py-12">
          <p className="text-gray-600">Loading pushed medicine sets...</p>
        </div>
      </Layout>
    )
  }

  if (medicineSets.length === 0) {
    return (
      <Layout title="Pushed Medicine Excel">
        <div className="card text-center py-12" data-testid="empty-state">
          <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pushed Medicine Sets</h3>
          <p className="text-gray-600 mb-4">Medicine sets will appear here after pushing to eHCMS</p>
          <Button onClick={() => window.location.href = '/all-sets'} data-testid="go-to-all-sets">
            Go to All Medicine Sets
          </Button>
        </div>
      </Layout>
    )
  }

  const handleReDownload = async (downloadId, filename) => {
    try {
      toast.info('Downloading Excel file...')
      const response = await axios.get(`${API}/excel-downloads/${downloadId}/re-download`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Excel file downloaded successfully!')
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download Excel file')
    }
  }

  const handleDeleteDownload = async (downloadId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return
    }

    try {
      await axios.delete(`${API}/excel-downloads/${downloadId}`)
      toast.success('Excel download deleted successfully!')
      setExcelDownloads(excelDownloads.filter(d => d.id !== downloadId))
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete excel download')
    }
  }

  return (
    <Layout title="Pushed Medicine Excel">
      {/* Tabs */}
      <div className="flex gap-2 mb-4 bg-white rounded-lg p-1 shadow w-fit">
        <button
          onClick={() => setActiveTab('sets')}
          className={`px-4 py-2 rounded ${activeTab === 'sets' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="tab-sets"
        >
          Medicine Sets ({medicineSets.length})
        </button>
        <button
          onClick={() => setActiveTab('downloads')}
          className={`px-4 py-2 rounded ${activeTab === 'downloads' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid="tab-downloads"
        >
          Excel Downloads ({excelDownloads.length})
        </button>
      </div>

      {activeTab === 'sets' && (
      <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-3 items-center">
          {selectedSets.length > 0 && (
            <>
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                data-testid="bulk-delete-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedSets.length})
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedSets([])}
                data-testid="clear-selection"
              >
                Clear Selection
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table data-testid="pushed-sets-table">
            <thead>
              <tr>
                <th className="w-12">
                  <Checkbox
                    checked={selectedSets.length === medicineSets.length && medicineSets.length > 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedSets(medicineSets.map(s => s.id))
                      } else {
                        setSelectedSets([])
                      }
                    }}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th>Set Name</th>
                <th>Medicines</th>
                <th>Pushed Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {medicineSets.map((set) => (
                <tr key={set.id}>
                  <td>
                    <Checkbox
                      checked={selectedSets.includes(set.id)}
                      onCheckedChange={(checked) => handleSelectSet(set.id, checked)}
                      data-testid={`checkbox-${set.id}`}
                    />
                  </td>
                  <td className="font-semibold" data-testid={`set-name-${set.id}`}>
                    <div className="flex items-center gap-2">
                      {set.set_name}
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Pushed</span>
                    </div>
                  </td>
                  <td>{set.medicines.length}</td>
                  <td>{set.pushed_at ? format(new Date(set.pushed_at), 'MMM dd, yyyy HH:mm') : '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewImage(set)}
                        data-testid={`view-image-${set.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleExport(set.id, set.set_name)}
                        data-testid={`export-${set.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(set.id, set.set_name)}
                        disabled={deletingId === set.id}
                        data-testid={`delete-${set.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {activeTab === 'downloads' && (
      <div className="card">
        <div className="table-container">
          <table data-testid="downloads-table">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Downloaded Date</th>
                <th>Medicine Sets</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {excelDownloads.map((download) => (
                <tr key={download.id}>
                  <td className="font-semibold" data-testid={`download-name-${download.id}`}>
                    {download.filename}
                  </td>
                  <td>{format(new Date(download.downloaded_at), 'MMM dd, yyyy HH:mm')}</td>
                  <td>{download.medicine_set_ids.length} set(s)</td>
                  <td>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleReDownload(download.id, download.filename)}
                        data-testid={`download-${download.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteDownload(download.id, download.filename)}
                        data-testid={`delete-download-${download.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {excelDownloads.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center text-gray-500 py-8">
                    No Excel downloads yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {showImageDialog && selectedSet?.image_base64 && (
        <AdvancedImageViewer
          imageBase64={selectedSet.image_base64}
          onClose={() => setShowImageDialog(false)}
          title={`${selectedSet.set_name} - Original Image`}
        />
      )}
    </Layout>
  )
}

export default PushedMedicineSets
